import React, { useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { X } from 'lucide-react';
import { TOKENS } from '../state/colors';
import { fs } from '../state/typography';
import { formatFechaSinHora, hoyMas15Bogota } from '../state/formatVersiones';
import { centroApi } from '../api/centroApi';
import { BolsaCentro, BolsaConReprecio, CentroApiError } from '../types';
import { BuscadorClientes, OpcionCliente } from './BuscadorClientes';

const texto = { fontFamily: 'Poppins, sans-serif' };

interface DuplicarModalProps {
  bolsa: BolsaCentro;
  onCerrar: () => void;
  onDuplicada: (resultado: BolsaConReprecio) => void;
  onError: (mensaje: string) => void;
}

// SPEC-206 (UC-009, design §7b) — cliente preseleccionado (el de la original) y cambiable con el
// buscador de SPEC-103; "Duplicar" queda deshabilitado mientras la petición está en curso.
export const DuplicarModal: React.FC<DuplicarModalProps> = ({ bolsa, onCerrar, onDuplicada, onError }) => {
  const original: OpcionCliente | null = bolsa.cliente_id
    ? { id: bolsa.cliente_id, razonSocial: bolsa.cliente_razon_social ?? 'Cliente', nit: bolsa.cliente_nit ?? null }
    : null;
  const [cliente, setCliente] = useState<OpcionCliente | null>(original);
  const [duplicando, setDuplicando] = useState(false);
  const codigo = bolsa.codigo_cotizacion ?? bolsa.id;

  const duplicar = async () => {
    setDuplicando(true);
    try {
      onDuplicada(await centroApi.duplicar(bolsa.id, cliente?.id ?? null));
    } catch (error) {
      onError(error instanceof CentroApiError ? error.message : 'No se pudo duplicar la cotización');
      setDuplicando(false);
    }
  };

  return (
    <Dialog open onClose={duplicando ? undefined : onCerrar} aria-labelledby="duplicar-titulo" fullWidth maxWidth="xs" data-testid="duplicar-modal">
      <DialogTitle id="duplicar-titulo" sx={{ ...texto, fontSize: fs(17), fontWeight: 700, color: TOKENS.ink, paddingRight: '48px' }}>
        Duplicar {codigo}
        <IconButton aria-label="Cerrar" onClick={onCerrar} disabled={duplicando} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <X size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ ...texto, fontSize: fs(11), fontWeight: 600, textTransform: 'uppercase', color: TOKENS.muted }}>Cliente</Typography>
        <BuscadorClientes testId="duplicar-cliente" valor={cliente} onSeleccionar={setCliente} deshabilitado={duplicando} />
        <Box sx={{ marginTop: '14px' }}>
          <Typography sx={{ ...texto, fontSize: fs(12.5), color: TOKENS.text }}>
            Se copiarán {bolsa.lineas_count} productos con precios del catálogo de hoy · válida hasta {formatFechaSinHora(hoyMas15Bogota())}
          </Typography>
          <Typography sx={{ ...texto, fontSize: fs(12), color: TOKENS.muted, marginTop: '4px' }}>La cotización original no cambia.</Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '0 24px 18px' }}>
        <Button onClick={onCerrar} disabled={duplicando} sx={{ ...texto, textTransform: 'none', color: TOKENS.text }}>Cancelar</Button>
        <Button
          data-testid="duplicar-confirmar"
          onClick={duplicar}
          disabled={duplicando || !cliente}
          startIcon={duplicando ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{ ...texto, textTransform: 'none', backgroundColor: TOKENS.purple, color: '#fff', borderRadius: '999px', padding: '6px 18px', '&:hover': { backgroundColor: TOKENS.purpleDark }, '&.Mui-disabled': { backgroundColor: TOKENS.faint, color: '#fff' } }}
        >
          Duplicar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
